import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Category from "./Category/Category";
import HeaderMenu from "./HeaderMenu/HeaderMenu";
import Items from "./Items/Items";
import Actions from "./Actions/Actions";
import CartPopup from "./Actions/Cart";
import "./MainMenu.css";

const SESSION_KEY = "fliplyn_stall_session";
const CART_KEY = "fliplyn_cart";

export default function MainMenuPage() {
  const location = useLocation();

  
  const [session, setSession] = useState(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

// ---------------- UI STATE ----------------
  const [menuItems, setMenuItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

// ---------------- RESOLVE STALL ----------------

  useEffect(() => {
    if (!location.state?.stallId) return;

    setSession((prev) => {
      if (!prev || prev.stallId !== location.state.stallId) {
        const savedCart =
          JSON.parse(localStorage.getItem(CART_KEY)) || {};

        return {
          stallId: location.state.stallId,
          stallName: location.state.stall?.name || "Food Stall",
          selectedCategory: "",
          cart: savedCart,
        };
      }
      return prev;
    });
  }, [location.state]);
                          
  // ---------------- PERSIST SESSION ----------------
  useEffect(() => {
    if (session) {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }, [session]);

  // ---------------- CART ADAPTER (FIXED) ----------------
  const setCart = useCallback((updater) => {
    setSession((prev) => {
      if (!prev) return prev;

      const nextCart =
        typeof updater === "function"
          ? updater(prev.cart || {})
          : updater;

      // ✅ SAVE CART TO LOCAL STORAGE
      localStorage.setItem(CART_KEY, JSON.stringify(nextCart));

      console.log("💾 Cart saved:", nextCart);

      return {
        ...prev,
        cart: nextCart,
      };
    });
  }, []);

  // ---------------- DEBUG ----------------
  useEffect(() => {
    if (!session) return;  
    console.log("🏪 Stall Loaded:", session.stallName);
    console.log("🛒 Cart State:", session.cart);
  }, [session]);

  // ---------------- GUARD ----------------
  if (!session) {
    return <div className="items-placeholder">No stall selected</div>;
  }

  // ---------------- DERIVED STATE ----------------
  const { stallId, stallName, selectedCategory } = session;
  const cart = session.cart || {};
  const itemCount = Object.values(cart).reduce((a, b) => a + b, 0);

  // ---------------- CART ACTIONS ----------------
  const handleAdd = (item) => {
    console.log("➕ Add item:", item.name);
    setCart((prev) => ({
      ...prev,
      [item.id]: (prev[item.id] || 0) + 1,
    }));
  };

  const handleRemove = (item) => {
    console.log("➖ Remove item:", item.name);
    setCart((prev) => {
      const updated = { ...prev };
      if (updated[item.id] > 1) updated[item.id] -= 1;
      else delete updated[item.id];
      return updated;
    });
  };

  const handleClear = () => {
    console.log("🧹 Clear cart");
    localStorage.removeItem(CART_KEY);
    setCart({});
  };

  // ---------------- RENDER ----------------
  return (
    <div className="burger-yard-page">
      <HeaderMenu
        stallName={stallName}
        cartCount={itemCount}
        onCartClick={() => setIsCartOpen(true)}
      />

      <div className="main-section">
        <Category
          stallId={stallId}
          selected={selectedCategory}
          onSelect={(catId) =>
            setSession((prev) => ({
              ...prev,
              selectedCategory: catId,
            }))
          }
        />

        {selectedCategory ? (
          <Items
            categoryId={selectedCategory}
            stallId={stallId}
            cart={cart}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onItemsLoaded={setMenuItems}
          />
        ) : (
          <div className="items-placeholder">
            Select a category to view items
          </div>
        )}
      </div>

      <Actions
        itemCount={itemCount}
        total={0}
        onClear={handleClear}
        onCheckout={() => setIsCartOpen(true)}
        onOpenCart={() => setIsCartOpen(true)}
        cart={cart}
        setCart={setCart}
      />

      <CartPopup
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        setCart={setCart}
        items={menuItems}
      />
    </div>
  );
}